import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormField,
  FormInput,
  FormSelect,
  Button,
} from "web-design-system";
import { ArrowRightIcon, ArrowLeftIcon, Loader2 } from "lucide-react";
import { useSubmitGiftKyc } from "creso-core/hooks";
import { OCCUPATION } from "creso-core/constants";
import { handleError } from "web-core/utils";
import { StepProps, GiftKycFormData } from "../../../types";

type FieldType = "business" | "salaried" | "others" | "none";

function getFieldType(occupation?: string): FieldType {
  if (occupation === "business") return "business";
  if (occupation === "services" || occupation === "professional")
    return "salaried";
  if (occupation === "others") return "others";
  return "none";
}

const formSchema = z
  .object({
    occupation: z.string().min(1, "Occupation is required"),
    ckycNumber: z
      .string()
      .regex(/^\d{14}$/, "CKYC number must be exactly 14 digits"),
    fatherName: z.string(),
    name: z.string().optional(),
    address: z.string().optional(),
    nature: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const fieldType = getFieldType(data.occupation);
    if (fieldType === "others" && !data.nature?.trim())
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please specify your occupation",
        path: ["nature"],
      });
    if (fieldType === "salaried") {
      if (!data.name?.trim())
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Name of employer is required",
          path: ["name"],
        });
      if (!data.address?.trim())
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Place of work is required",
          path: ["address"],
        });
    }
    if (fieldType === "business") {
      if (!data.name?.trim())
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Name of business is required",
          path: ["name"],
        });
      if (!data.nature?.trim())
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Nature of business is required",
          path: ["nature"],
        });
      if (!data.address?.trim())
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Registered address is required",
          path: ["address"],
        });
    }
  });

type FormValues = z.infer<typeof formSchema>;

const FIELD_LABELS: Record<
  "business" | "salaried",
  { name: string; address: string; nature?: string }
> = {
  business: {
    name: "Name of Business",
    address: "Registered Address",
    nature: "Nature of Business",
  },
  salaried: { name: "Name of Employer", address: "Place of Work" },
};

export function KycOccupationStep({ formData, onNext, onBack }: StepProps) {
  const { mutateAsync: submitGiftKyc, isPending } = useSubmitGiftKyc();
  const { selectedClient, amc } = formData;
  const investmentAccountGuid = selectedClient?.investment_account ?? "";

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      occupation: formData.occupationType,
      ckycNumber: formData.ckycNumber,
      fatherName: formData.fatherName,
      name: formData.companyName,
      address: formData.companyAddress,
      nature: formData.natureOfBusiness,
    },
  });

  const occupation = form.watch("occupation");
  const fieldType = getFieldType(occupation);

  const onSubmit = async (values: FormValues) => {
    try {
      const response = await submitGiftKyc({
        investment_account_guid: investmentAccountGuid,
        amc,
        ckyc_number: values.ckycNumber,
        father_name: values.fatherName,
        occupation: values.occupation,
        // A fresh upload (File) or the URL of a previously submitted document
        pan_card_image: formData.panCard,
        address_proof_image: formData.maskedAadhaar,
        cancelled_cheque_image: formData.cancelledCheque,
        pan_card_password: formData.panCardPassword,
        address_proof_password: formData.maskedAadhaarPassword,
        cancelled_cheque_password: formData.cancelledChequePassword,
        name_of_company: values.name,
        company_address: values.address,
        nature_of_business: values.nature,
      });
      onNext({
        occupationType: values.occupation,
        ckycNumber: values.ckycNumber,
        companyName: values.name,
        companyAddress: values.address,
        natureOfBusiness: values.nature,
        responseForm: response.form_url,
        formId: response.form_id,
      } satisfies Partial<GiftKycFormData>);
    } catch (e) {
      handleError(e, "Failed to create account opening form.");
    }
  };

  return (
    <div className="flex flex-col h-full gap-8">
      <div className="flex items-center gap-3">
        <Button
          type="button"
          className="-ml-2"
          variant="ghost"
          size="icon"
          onClick={onBack}
          disabled={isPending}
        >
          <ArrowLeftIcon className="size-4" />
        </Button>
        <span className="text-2xl font-bold text-foreground">
          <span className="text-gift-foreground">CKYC</span> &amp; Other details
        </span>
      </div>
      <Form {...form}>
        <form
          className="flex flex-col gap-6 flex-1"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col">
            <span className="text-sm font-bold mb-1 text-foreground">CKYC</span>
            <span className="text-xs text-muted-foreground mb-3">
              Central Know Your Customer number issued by CERSAI.{" "}
              <a
                href="https://www.ckycindia.in/kyc/getkyccard"
                target="_blank"
                rel="noreferrer"
                className="text-foreground font-semibold"
              >
                How to find yours?
              </a>
            </span>
            <FormField
              control={form.control}
              name="ckycNumber"
              render={({ field }) => (
                <FormInput
                  label=""
                  placeholder="Enter your 14 digit CKYC number"
                  {...field}
                />
              )}
            />
            <span className="text-xs mt-3 text-muted-foreground">
              Need further help?{" "}
              <a
                className="text-primary font-semibold"
                href="https://youtu.be/llT6mkGHMlg"
                target="_blank"
                rel="noreferrer"
              >
                Watch video
              </a>
            </span>
          </div>

          <FormField
            control={form.control}
            name="fatherName"
            render={({ field }) => (
              <FormInput
                label="Father's name"
                placeholder="Enter your father's name"
                {...field}
              />
            )}
          />

          <FormField
            control={form.control}
            name="occupation"
            render={({ field }) => (
              <FormSelect
                label="Occupation"
                options={OCCUPATION}
                placeholder="Select occupation"
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                  form.setValue("name", "");
                  form.setValue("address", "");
                  form.setValue("nature", "");
                }}
              />
            )}
          />

          {(fieldType === "business" || fieldType === "salaried") && (
            <>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormInput
                    label={FIELD_LABELS[fieldType].name}
                    placeholder="Enter name"
                    {...field}
                    value={field.value ?? ""}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormInput
                    label={FIELD_LABELS[fieldType].address}
                    placeholder="Enter address"
                    {...field}
                    value={field.value ?? ""}
                  />
                )}
              />
            </>
          )}

          {(fieldType === "business" || fieldType === "others") && (
            <FormField
              control={form.control}
              name="nature"
              render={({ field }) => (
                <FormInput
                  label={
                    fieldType === "others"
                      ? "Please specify"
                      : FIELD_LABELS.business.nature!
                  }
                  placeholder={
                    fieldType === "others"
                      ? "Enter your occupation"
                      : "e.g. Manufacturing, Construction"
                  }
                  {...field}
                  value={field.value ?? ""}
                />
              )}
            />
          )}

          <div className="flex items-center mt-auto">
            <Button type="submit" size="lg" disabled={isPending}>
              {isPending ? "Submitting…" : "Continue"}
              {isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ArrowRightIcon className="size-4" />
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
